import axios, { AxiosInstance } from 'axios';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { apiBaseUrl, publicApiPath, protectedApiPath } from '../config/ServiceConfig';

class ApiService {
    private httpClient: AxiosInstance;
    private apiGatewayUrlParam: string; // Legacy support
    private apiBaseUrl: string;
    private publicApiPath: string;
    private protectedApiPath: string;
    private requestCache: Map<string, Promise<any>> = new Map();

    constructor(apiGatewayUrlParam: string, httpClient: AxiosInstance = axios) {
        this.apiGatewayUrlParam = apiGatewayUrlParam; // Legacy support
        this.httpClient = httpClient;
        this.apiBaseUrl = apiBaseUrl;
        this.publicApiPath = publicApiPath;
        this.protectedApiPath = protectedApiPath;
    }

    /**
     * Determines whether an endpoint should use the public or protected API
     */
    private getApiPath(endpoint: string, method: string, skipAuth: boolean = false): string {
        // Auth endpoints routing logic
        if (endpoint.startsWith('auth/')) {
            // If skipAuth is true, route specific auth endpoints to public API
            if (skipAuth) {
                // Only certain auth endpoints are allowed on public API
                if (endpoint.startsWith('auth/identity-providers/check')) {
                    return this.publicApiPath;
                }
                // Other auth endpoints with skipAuth still go to protected API
                // (they'll just not include auth headers)
                return this.protectedApiPath;
            }
            // Auth endpoints with authentication go to protected API
            return this.protectedApiPath;
        }

        // If skipAuth is explicitly set, use public API
        if (skipAuth) {
            return this.publicApiPath;
        }

        // Define public endpoints (no authentication required)
        const publicEndpoints = [
            // User profile endpoints (GET only)
            /^users\/[^\/]+\/profile$/,
            /^users\/[^\/]+\/profile\/avatar$/,
            /^users\/[^\/]+\/kandis$/,
            
            // Username availability check
            /^users\/username-availability\/[^\/]+$/,
            
            // Kandi events (GET only)
            /^kandis\/[^\/]+\/events$/,
            
            // Vanity handles (GET only)
            /^vanityHandles\/[^\/]+$/,
            
            // Tags (GET only)
            /^tags\/[^\/]+$/
        ];

        // Check if this is a GET request to a public endpoint
        if (method.toUpperCase() === 'GET') {
            for (const pattern of publicEndpoints) {
                if (pattern.test(endpoint)) {
                    return this.publicApiPath;
                }
            }
        }

        // Everything else goes to protected API
        return this.protectedApiPath;
    }

    /**
     * Builds the full URL for an API request
     */
    private buildUrl(endpoint: string, method: string, skipAuth: boolean = false): string {
        const apiPath = this.getApiPath(endpoint, method, skipAuth);
        const fullUrl = `${this.apiBaseUrl}${apiPath}${endpoint}`;
        
        console.log('🔍 [DEBUG] ApiService URL routing:', {
            endpoint,
            method,
            skipAuth,
            apiPath,
            fullUrl
        });
        
        return fullUrl;
    }

    private async getAuthHeaders(): Promise<{ headers: { Authorization?: string } }> {
        console.log('🔍 [DEBUG] ApiService.getAuthHeaders - Starting auth header retrieval - Stack trace:');
        console.trace();
        
        try {
            // Method 1: Try to get current session using v6 API
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (idToken) {
                console.log('🔍 [DEBUG] Using current session token from v6 API');
                return { headers: { 'Authorization': idToken } };
            }
        } catch (err: any) {
            console.log('🔍 [DEBUG] Current session failed:', err.message);
        }

        // Method 2: Try to get current user and then session
        try {
            const currentUser = await getCurrentUser();
            if (currentUser) {
                console.log('🔍 [DEBUG] Found authenticated user, attempting to get session');
                const session = await fetchAuthSession();
                const idToken = session.tokens?.idToken?.toString();
                
                if (idToken) {
                    console.log('🔍 [DEBUG] Using authenticated user session token');
                    return { headers: { 'Authorization': idToken } };
                }
            }
        } catch (err: any) {
            console.log('🔍 [DEBUG] Authenticated user method failed:', err.message);
        }

        // Method 3: Try stored session fallback (for backward compatibility)
        try {
            const storedSession = localStorage.getItem('amplify-auth-session');
            if (storedSession) {
                const authData = JSON.parse(storedSession);
                
                // Verify token is still valid
                const tokenPayload = JSON.parse(atob(authData.idToken.split('.')[1]));
                const now = Math.floor(Date.now() / 1000);
                
                if (tokenPayload.exp && tokenPayload.exp > now) {
                    console.log('🔍 [DEBUG] Using stored auth session for API request');
                    return { headers: { 'Authorization': authData.idToken } };
                } else {
                    console.log('🔍 [DEBUG] Stored auth session expired, clearing it');
                    localStorage.removeItem('amplify-auth-session');
                }
            }
        } catch (error) {
            console.error('❌ [DEBUG] Error reading stored auth session:', error);
        }
        
        console.error('❌ [DEBUG] All authentication methods failed');
        return { headers: {} };
    }

    async getRequest(apiURL: string, params: any = {}): Promise<any> {
        const fullUrl = this.buildUrl(apiURL, 'GET');
        console.log('🔍 [DEBUG] ApiService GET request:', { url: fullUrl, params });
        
        try {
            // Determine if auth is needed based on the API path
            const apiPath = this.getApiPath(apiURL, 'GET');
            const needsAuth = apiPath === this.protectedApiPath;
            
            const authHeaders = needsAuth ? await this.getAuthHeaders() : { headers: {} };
            console.log('🔍 [DEBUG] Auth headers:', needsAuth ? (authHeaders.headers.Authorization ? 'Present' : 'Missing') : 'Not needed');
            
            const apiRes = await this.httpClient.get(fullUrl, { ...authHeaders, params });
            console.log('🔍 [DEBUG] GET response status:', apiRes.status);
            console.log('🔍 [DEBUG] GET response data:', apiRes.data);
            return apiRes.data;
        } catch (error: any) {
            console.error('❌ [DEBUG] GET request failed:', {
                url: fullUrl,
                params,
                error: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            
            // Check if this is an auth-related error that might trigger sign-out
            if (error.response?.status === 401 || error.response?.status === 403) {
                console.log('🚨 [DEBUG] AUTH ERROR DETECTED - This might trigger automatic sign-out!');
                console.trace();
            }
            
            throw error;
        }
    }

    async postRequest(apiURL: string, data: any = {}, skipAuth: boolean = false): Promise<any> {
        const fullUrl = this.buildUrl(apiURL, 'POST', skipAuth);
        console.log('🔍 [DEBUG] ApiService POST request:', { url: fullUrl, data, skipAuth });
        
        try {
            const authHeaders = skipAuth ? { headers: {} } : await this.getAuthHeaders();
            console.log('🔍 [DEBUG] Auth headers:', skipAuth ? 'Skipped' : (authHeaders.headers.Authorization ? 'Present' : 'Missing'));
            
            const apiRes = await this.httpClient.post(fullUrl, data, authHeaders);
            console.log('🔍 [DEBUG] POST response status:', apiRes.status);
            console.log('🔍 [DEBUG] POST response data:', apiRes.data);
            return apiRes.data;
        } catch (error: any) {
            console.error('❌ [DEBUG] POST request failed:', {
                url: fullUrl,
                requestData: data,
                error: error.message,
                status: error.response?.status,
                responseData: error.response?.data
            });
            throw error;
        }
    }

    async putRequest(apiURL: string, data: any = {}): Promise<any> {
        const fullUrl = this.buildUrl(apiURL, 'PUT');
        console.log('🔍 [DEBUG] ApiService PUT request:', { url: fullUrl, data });
        
        try {
            const authHeaders = await this.getAuthHeaders();
            console.log('🔍 [DEBUG] PUT Auth headers:', authHeaders.headers.Authorization ? 'Present' : 'Missing');
            
            const apiRes = await this.httpClient.put(fullUrl, data, authHeaders);
            console.log('🔍 [DEBUG] PUT response status:', apiRes.status);
            console.log('🔍 [DEBUG] PUT response data:', apiRes.data);
            return apiRes.data;
        } catch (error: any) {
            console.error('❌ [DEBUG] PUT request failed:', {
                url: fullUrl,
                requestData: data,
                error: error.message,
                status: error.response?.status,
                responseData: error.response?.data
            });
            
            if (error.response?.data?.message) {
                throw new Error(error.response.data.message);
            }
            
            throw error;
        }
    }

    // Onboarding API methods
    async checkOnboardingStatus(username: string): Promise<{completed: boolean, completedAt: string | null}> {
        const cacheKey = `onboarding-${username}`;
        
        // Check if there's already a request in progress
        if (this.requestCache.has(cacheKey)) {
            console.log('🔍 [DEBUG] Using cached onboarding request for:', username);
            return this.requestCache.get(cacheKey)!;
        }
        
        // Create new request and cache it
        const requestPromise = this.getRequest(`users/${username}/profile/onboarding`);
        this.requestCache.set(cacheKey, requestPromise);
        
        try {
            const result = await requestPromise;
            return result;
        } finally {
            // Clear cache after request completes
            this.requestCache.delete(cacheKey);
        }
    }

    async completeOnboarding(username: string): Promise<any> {
        return await this.putRequest(`users/${username}/profile/onboarding`, { completed: true });
    }

    // Username validation methods
    async checkUsernameAvailability(username: string): Promise<{available: boolean, message?: string}> {
        try {
            return await this.getRequest(`users/username-availability/${encodeURIComponent(username)}`);
        } catch (error: any) {
            if (error.response?.status === 404) {
                // Username not found means it's available
                return { available: true };
            }
            throw error;
        }
    }

    // Avatar/display name methods
    async getUserAvatar(username: string): Promise<{handle: string, bio: string | null, pronouns: string | null, location: string | null}> {
        return await this.getRequest(`users/${username}/profile/avatar`);
    }
}

export default ApiService;
